/**
 * Sprint 16 · S16.1 · Sidebar (Desktop ≥ 768px) für operator_admin/super_admin.
 *
 * Mockup-Quelle: mockups/LEADCraft-Mockups.html § dashboard (d-sidebar).
 * Sektionen: Übersicht · Verwaltung · Einstellungen.
 * Tenant-Pill mit Logo (oder Initial) + Rolle.
 * Footer: Avatar + Name + Rolle + Logout.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity,
  Building2,
  ChevronLeft,
  ChevronRight,
  Coins,
  FormInput,
  Inbox,
  LayoutDashboard,
  LogOut,
  Mail,
  Palette,
  Pin,
  PinOff,
  Server,
  Sparkles,
  Users,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { APP_VERSION, BUILD_TIMESTAMP } from '../lib/version';

interface NavItem {
  to: string;
  label: string;
  Icon: typeof LayoutDashboard;
  /** Sprint 38 · Wenn gesetzt, nur für diese Rollen sichtbar. */
  roles?: Array<'super_admin' | 'operator_admin' | 'dispatcher' | 'employee'>;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    title: 'Übersicht',
    items: [
      { to: '/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
      { to: '/leads', label: 'Leads', Icon: Inbox },
    ],
  },
  {
    title: 'Verwaltung',
    items: [
      { to: '/users', label: 'Mitarbeiter', Icon: Users },
      { to: '/admin/forms', label: 'Formulare', Icon: FormInput },
      { to: '/settings/mail-templates', label: 'Mail-Templates', Icon: Mail },
      // Sprint 64 · KI-Prompts pro Tenant editierbar
      { to: '/settings/ai-prompts', label: 'KI-Prompts', Icon: Sparkles },
    ],
  },
  {
    title: 'Einstellungen',
    items: [
      { to: '/settings/branding', label: 'Branding', Icon: Palette },
      // Sprint 67 · Mail-Einstellungen jetzt fuer operator_admin sichtbar
      // (kann eigene Tenant-Versand-Adresse einrichten · Default bleibt geschuetzt).
      { to: '/settings/mail', label: 'Mail', Icon: Server },
    ],
  },
  // Sprint 42 · Eigene Sektion für Super-Admin · sichtbar nur für super_admin
  {
    title: 'System (Super-Admin)',
    items: [
      { to: '/admin/tenants', label: 'Tenants', Icon: Building2, roles: ['super_admin'] },
      { to: '/admin/health', label: 'System-Health', Icon: Activity, roles: ['super_admin'] },
      { to: '/admin/ai-usage', label: 'AI-Kosten', Icon: Coins, roles: ['super_admin'] },
    ],
  },
];

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super-Admin',
  operator_admin: 'Operator-Admin',
  dispatcher: 'Dispatcher',
  employee: 'Mitarbeiter',
};

// Sprint 71 · Sidebar-Pin
// localStorage-Key + Auto-Default je Viewport-Width
const PIN_KEY = 'leadcraft_sidebar_pinned';

function readPinPref(): boolean {
  if (typeof window === 'undefined') return true;
  const stored = window.localStorage.getItem(PIN_KEY);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  // Auto: pinned bei >= 1280px (Desktop + Tablet-Landscape), unpinned darunter
  return window.innerWidth >= 1280;
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [pinned, setPinned] = useState<boolean>(readPinPref);
  const [hoverExpand, setHoverExpand] = useState(false);

  // Auf Route-Wechsel hover-overlay schliessen
  useEffect(() => {
    setHoverExpand(false);
  }, [location.pathname]);

  // Sprint 71 · body[data-sidebar-pinned] damit Layout-CSS padding-left anpasst
  useEffect(() => {
    document.body.dataset.sidebarPinned = String(pinned);
    return () => {
      delete document.body.dataset.sidebarPinned;
    };
  }, [pinned]);

  function togglePin(): void {
    setPinned((p) => {
      const next = !p;
      try {
        window.localStorage.setItem(PIN_KEY, String(next));
      } catch { /* ignore */ }
      return next;
    });
  }

  if (!user) return null;
  if (user.role !== 'operator_admin' && user.role !== 'super_admin') return null;

  const initials = user.full_name
    .split(' ')
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');

  const tenantName = user.branding.tenant_name ?? 'TENANT';
  const tenantInitial = tenantName.charAt(0).toUpperCase();
  const roleLabel = ROLE_LABELS[user.role] ?? user.role;

  // Sprint 71 · 3 visuelle Zustaende:
  // pinned       → expanded sticky (Default Desktop)
  // !pinned      → collapsed (nur Icons, 56px)
  // !pinned + hoverExpand → expanded als Overlay (Touch: per Chevron-Click)
  const isExpanded = pinned || hoverExpand;
  const sidebarClass = [
    'lc-sidebar',
    !isExpanded ? 'lc-sidebar--collapsed' : '',
    !pinned && hoverExpand ? 'lc-sidebar--overlay' : '',
  ].filter(Boolean).join(' ');

  return (
    <aside
      className={sidebarClass}
      aria-label="Hauptnavigation"
      onMouseLeave={() => { if (!pinned) setHoverExpand(false); }}
    >
      {/* Sprint 71 · Pin + Toggle */}
      <div className="lc-sidebar__controls">
        {isExpanded ? (
          <button
            type="button"
            className="lc-sidebar__pin-btn"
            onClick={togglePin}
            title={pinned ? 'Sidebar lösen (collapsed)' : 'Sidebar anpinnen'}
            aria-label={pinned ? 'Anpinnung lösen' : 'Sidebar anpinnen'}
          >
            {pinned ? <Pin size={14} /> : <PinOff size={14} />}
          </button>
        ) : null}
        <button
          type="button"
          className="lc-sidebar__toggle-btn"
          onClick={() => {
            if (pinned) {
              // pinned → unpin + collapse
              togglePin();
              setHoverExpand(false);
            } else if (hoverExpand) {
              // overlay → close
              setHoverExpand(false);
            } else {
              // collapsed → expand (Touch-tauglich)
              setHoverExpand(true);
            }
          }}
          title={isExpanded ? 'Einklappen' : 'Ausklappen'}
          aria-label={isExpanded ? 'Sidebar einklappen' : 'Sidebar ausklappen'}
        >
          {isExpanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>

      <div className="lc-sidebar__logo">
        LEAD<span>Craft</span>
      </div>
      <div className="lc-sidebar__claim">ROBOTICS · LEAD CAPTURE</div>

      <div className="lc-sidebar__pill">
        {user.branding.logo_url ? (
          <img src={user.branding.logo_url} alt={tenantName} />
        ) : (
          <span className="lc-sidebar__pill-fallback" aria-hidden="true">
            {tenantInitial}
          </span>
        )}
        <div className="lc-sidebar__pill-meta">
          <div className="lc-sidebar__pill-name">{tenantName}</div>
          <div className="lc-sidebar__pill-role">{roleLabel}</div>
        </div>
      </div>

      <nav className="lc-sidebar__nav">
        {SECTIONS.map((section) => {
          const visibleItems = section.items.filter(
            (item) => !item.roles || item.roles.includes(user.role),
          );
          if (visibleItems.length === 0) return null;
          return (
          <div key={section.title} className="lc-sidebar__group">
            <div className="lc-sidebar__group-title">{section.title}</div>
            {visibleItems.map(({ to, label, Icon }) => {
              const active =
                location.pathname === to ||
                (to !== '/dashboard' && location.pathname.startsWith(to));
              return (
                <Link
                  key={to}
                  to={to}
                  className={`lc-sidebar__item ${
                    active ? 'lc-sidebar__item--active' : ''
                  }`}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon size={16} strokeWidth={2} aria-hidden="true" />
                  <span>{label}</span>
                </Link>
              );
            })}
          </div>
          );
        })}
      </nav>

      <div className="lc-sidebar__footer">
        <Link to="/profile" className="lc-sidebar__avatar" aria-label="Mein Profil">
          {user.profile_image_url ? (
            <img src={user.profile_image_url} alt="" />
          ) : (
            <span className="lc-sidebar__avatar-initials">{initials || '?'}</span>
          )}
        </Link>
        <div className="lc-sidebar__user">
          <div className="lc-sidebar__user-name">{user.full_name}</div>
          <div className="lc-sidebar__user-role">{roleLabel}</div>
        </div>
        <button
          type="button"
          className="lc-sidebar__logout"
          onClick={() => void logout()}
          aria-label="Abmelden"
        >
          <LogOut size={14} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      {/* Sprint 32 · App-Version sichtbar im Sidebar-Footer */}
      <div className="lc-sidebar__version" title={`Build ${BUILD_TIMESTAMP}`}>
        v{APP_VERSION}
      </div>
    </aside>
  );
}
